import React from "react";
import { Check, Clock, Package, Bike, Home, X } from "lucide-react";

/**
 * Compact horizontal stepper showing the lifecycle of an online order.
 * Steps: Placed → Packing → Out for Delivery → Delivered
 */

const STEPS = [
  { key: "placed", label: "Placed", icon: Clock, statuses: ["pending", "paid"] },
  { key: "packing", label: "Packing", icon: Package, statuses: ["preparing", "packing", "ready", "assigned"] },
  { key: "out_for_delivery", label: "On the way", icon: Bike, statuses: ["out_for_delivery"] },
  { key: "delivered", label: "Delivered", icon: Home, statuses: ["delivered"] },
];

function currentStepIndex(status) {
  for (let i = STEPS.length - 1; i >= 0; i--) {
    if (STEPS[i].statuses.includes(status)) return i;
  }
  return 0;
}

export default function OrderTimeline({ status }) {
  const isCancelled = status === "cancelled";
  const activeIdx = currentStepIndex(status);

  if (isCancelled) {
    return (
      <div className="flex items-center gap-2 text-sm text-[#C62828] bg-[#FBE7E7] border border-[#F1B8B8] rounded-lg px-3 py-2" data-testid="order-timeline-cancelled">
        <X className="h-4 w-4" />
        <span className="font-semibold">Cancelled</span>
      </div>
    );
  }

  return (
    <div className="w-full" data-testid="order-timeline">
      <div className="relative flex items-center justify-between">
        {/* progress bar background */}
        <div className="absolute left-0 right-0 top-3 sm:top-4 h-0.5 bg-[#E1E5DF] -z-0" />
        {/* progress bar filled */}
        <div
          className="absolute left-0 top-3 sm:top-4 h-0.5 bg-[#2F5233] -z-0 transition-all duration-700"
          style={{ width: `${(activeIdx / (STEPS.length - 1)) * 100}%` }}
        />

        {STEPS.map((step, idx) => {
          const Icon = step.icon;
          const reached = idx <= activeIdx;
          const isCurrent = idx === activeIdx && status !== "delivered";
          return (
            <div key={step.key} className="relative z-10 flex flex-col items-center flex-1" data-testid={`order-timeline-step-${step.key}`}>
              <div
                className={[
                  "h-7 w-7 sm:h-9 sm:w-9 rounded-full flex items-center justify-center border-2 transition-all",
                  reached ? "bg-[#2F5233] border-[#2F5233] text-white" : "bg-white border-[#E1E5DF] text-[#9CA8A2]",
                  isCurrent ? "ring-4 ring-[#2F5233]/15 animate-pulse" : "",
                ].join(" ")}
              >
                {reached && idx < activeIdx ? <Check className="h-3.5 w-3.5 sm:h-4 sm:w-4" /> : <Icon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />}
              </div>
              <div
                className={[
                  "mt-1.5 text-[10px] sm:text-xs font-semibold leading-tight text-center",
                  reached ? "text-[#1C2826]" : "text-[#9CA8A2]",
                ].join(" ")}
              >
                {step.label}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
